import Phaser from 'phaser';
import { GAME_WIDTH, GAME_HEIGHT, PLAYER_COLORS } from '../config/Constants';
import { CLASSES, ClassDefinition } from '../data/Classes';
import { SPECIALS, SpecialDefinition } from '../data/Specials';
import { TorneoConfig } from './MenuScene';
import { TorneoSystem } from '../systems/TorneoSystem';
import { createEscapeMenu } from '../utils/EscapeMenu';

export interface PlayerSelection {
  isHuman: boolean;
  className: string;
  specialName: string;
  teamId: number;
}

export class SelectionScene extends Phaser.Scene {
  private config!: TorneoConfig;
  private currentPlayer: number = 0;
  private selections: PlayerSelection[] = [];
  private totalPlayers: number = 0;

  // UI elements
  private classCards: Phaser.GameObjects.Container[] = [];
  private specialCards: Phaser.GameObjects.Container[] = [];
  private statusText!: Phaser.GameObjects.Text;
  private selectedClass: string | null = null;
  private selectedSpecial: string | null = null;
  private phase: 'class' | 'special' = 'class';
  private labelPhase!: Phaser.GameObjects.Text;
  private phaseTitle!: Phaser.GameObjects.Text | null; // título "SELECCIONA CLASE/ESPECIAL"
  private bgGraphics!: Phaser.GameObjects.Graphics;
  private playerIndicator!: Phaser.GameObjects.Graphics;
  private previewTopGraphics!: Phaser.GameObjects.Graphics;
  private previewTopLabel!: Phaser.GameObjects.Text | null;
  private isTorneo: boolean = false;
  private torneoSystem: TorneoSystem | null = null;
  private detailPanel!: Phaser.GameObjects.Container;
  private detailPanelBg!: Phaser.GameObjects.Graphics;

  constructor() {
    super({ key: 'SelectionScene' });
  }

  init(data: { config: TorneoConfig; isTorneo?: boolean }): void {
    this.config = data.config;
    this.isTorneo = data.isTorneo || false;
    this.torneoSystem = null;
    this.currentPlayer = 0;
    this.phase = 'class';
    this.selectedClass = null;
    this.selectedSpecial = null;
    this.classCards = [];
    this.specialCards = [];

    // Calcular total de jugadores
    if (this.config.modo === 'individual') {
      this.totalPlayers = this.config.participantes;
    } else {
      this.totalPlayers = this.config.tamanoEquipo * 2;
    }

    // Crear selecciones
    this.selections = [];
    const humanosAsignados = Math.min(this.config.jugadoresHumanos, this.totalPlayers);
    for (let i = 0; i < this.totalPlayers; i++) {
      const teamId = this.config.modo === 'equipos' ? (i < this.totalPlayers / 2 ? 0 : 1) : i;
      this.selections.push({
        isHuman: i < humanosAsignados,
        className: '',
        specialName: '',
        teamId,
      });
    }
  }

  create(): void {
    this.cameras.main.setBackgroundColor('#080818');

    // Fondo con gradiente simulado
    this.bgGraphics = this.add.graphics();
    this.drawBackground();

    // Título con estilo
    const titleBar = this.add.graphics();
    titleBar.fillStyle(0x1a1a3e, 0.8);
    titleBar.fillRect(0, 0, GAME_WIDTH, 100);

    this.add.text(GAME_WIDTH / 2, 25, '⚔  PERSONALIZA TU PEONZA  ⚔', {
      fontSize: '24px',
      fontFamily: 'Arial Black, Impact, sans-serif',
      color: '#ffffff',
      letterSpacing: 4,
    }).setOrigin(0.5);

    // Estado del jugador
    this.statusText = this.add.text(GAME_WIDTH / 2, 55, '', {
      fontSize: '18px',
      fontFamily: 'Arial',
      color: '#f1c40f',
      fontStyle: 'bold',
    }).setOrigin(0.5);

    // Indicador de fase
    this.labelPhase = this.add.text(GAME_WIDTH / 2, 78, '', {
      fontSize: '14px',
      fontFamily: 'Arial',
      color: '#3498db',
    }).setOrigin(0.5);

    // Panel de detalle lateral
    this.createDetailPanel();

    // Preview de la peonza seleccionada
    this.previewTopGraphics = this.add.graphics();
    this.previewTopLabel = null;
    this.phaseTitle = null;

    // Escape menú
    createEscapeMenu(this,
      () => {}, // resume (el menú ya reanuda solo)
      () => this.scene.start('TitleScene')
    );

    // Botón volver
    this.createBackButton();

    this.showCurrentPlayer();
  }

  private createBackButton(): void {
    const backBtn = this.add.text(35, 8, '← VOLVER', {
      fontSize: '14px', color: '#e74c3c', fontStyle: 'bold',
    }).setOrigin(0, 0).setInteractive({ useHandCursor: true }).setDepth(10);
    backBtn.on('pointerdown', () => {
      if (this.phase === 'special') {
        // Volver a selección de clase
        this.specialCards.forEach((c) => c.destroy());
        this.specialCards = [];
        this.classCards.forEach((c) => {
          c.setVisible(true);
          c.setAlpha(0);
          this.tweens.add({ targets: c, alpha: 1, duration: 200 });
        });
        this.phase = 'class';
        this.selectedSpecial = null;
        this.labelPhase.setText('PASO 1: ELIGE UNA CLASE');
        this.previewTopGraphics.clear();
        if (this.previewTopLabel) {
          this.previewTopLabel.destroy();
          this.previewTopLabel = null;
        }
      } else {
        // Volver al menú anterior
        this.scene.start('MenuScene', { modo: this.isTorneo ? 'torneo' : 'rapida' });
      }
    });
  }

  private drawBackground(): void {
    this.bgGraphics.clear();
    // Líneas decorativas sutiles
    this.bgGraphics.lineStyle(1, 0x1a1a4e, 0.3);
    for (let i = 0; i < 20; i++) {
      const y = i * 40;
      this.bgGraphics.beginPath();
      this.bgGraphics.moveTo(0, y);
      this.bgGraphics.lineTo(GAME_WIDTH, y + 40);
      this.bgGraphics.strokePath();
    }
  }

  private createDetailPanel(): void {
    this.detailPanel = this.add.container(GAME_WIDTH - 260, 110);
    this.detailPanelBg = this.add.graphics();
    this.detailPanelBg.fillStyle(0x0d0d2b, 0.9);
    this.detailPanelBg.fillRoundedRect(0, 0, 250, 360, 12);
    this.detailPanelBg.lineStyle(2, 0x3498db, 0.4);
    this.detailPanelBg.strokeRoundedRect(0, 0, 250, 360, 12);
    this.detailPanel.add(this.detailPanelBg);

    this.add.text(GAME_WIDTH - 135, 120, '📊 DETALLES', {
      fontSize: '14px', color: '#3498db', fontStyle: 'bold',
    }).setOrigin(0.5, 0);
  }

  private showCurrentPlayer(): void {
    // Limpiar UI anterior
    this.classCards.forEach((c) => c.destroy());
    this.specialCards.forEach((c) => c.destroy());
    this.classCards = [];
    this.specialCards = [];
    this.previewTopGraphics.clear();
    if (this.previewTopLabel) {
      this.previewTopLabel.destroy();
      this.previewTopLabel = null;
    }
    this.clearDetailPanel();
    if (this.phaseTitle) {
      this.phaseTitle.destroy();
      this.phaseTitle = null;
    }
    this.phase = 'class';
    this.selectedClass = null;
    this.selectedSpecial = null;

    if (this.currentPlayer >= this.totalPlayers) {
      this.finishSelection();
      return;
    }

    const sel = this.selections[this.currentPlayer];
    const playerLabel = sel.isHuman ? `Jugador ${this.currentPlayer + 1}` : `IA ${this.currentPlayer + 1}`;
    const teamLabel = this.config.modo === 'equipos' ? ` (Equipo ${sel.teamId + 1})` : '';
    const playerColor = PLAYER_COLORS[this.currentPlayer % PLAYER_COLORS.length];
    const colorName = ['Azul', 'Rojo', 'Verde', 'Amarillo'][this.currentPlayer % 4];
    this.statusText.setText(`${playerLabel}${teamLabel}`);

    // Indicador de color del jugador
    this.playerIndicator = this.add.graphics();
    this.playerIndicator.fillStyle(playerColor, 0.8);
    this.playerIndicator.fillCircle(35, 55, 10);
    this.playerIndicator.fillStyle(0xffffff, 0.3);
    this.playerIndicator.fillCircle(35, 55, 5);

    if (!sel.isHuman) {
      this.selectRandomForIA(this.currentPlayer);
      return;
    }

    this.labelPhase.setText('PASO 1: ELIGE UNA CLASE');
    this.showClassSelection();
  }

  private clearDetailPanel(): void {
    // Copia segura de los hijos (excluyendo el fondo)
    const children = [...this.detailPanel.list];
    children.forEach((child) => {
      if (child !== this.detailPanelBg) {
        child.destroy();
      }
    });
  }

  private showClassSelection(): void {
    const startX = 30;
    const startY = 125;
    const cardW = 165;
    const cardH = 190;
    const cols = 5;
    const gapX = 12;
    const gapY = 12;

    // Título de sección (destruir el anterior si existe)
    if (this.phaseTitle) this.phaseTitle.destroy();
    this.phaseTitle = this.add.text(30, 103, 'SELECCIONA CLASE', {
      fontSize: '13px', color: '#7f8c8d', fontStyle: 'bold', letterSpacing: 2,
    });

    CLASSES.forEach((cls, i) => {
      const col = i % cols;
      const row = Math.floor(i / cols);
      const x = startX + col * (cardW + gapX);
      const y = startY + row * (cardH + gapY);

      const container = this.add.container(x, y);

      // Sombra
      const shadow = this.add.rectangle(3, 3, cardW, cardH, 0x000000, 0.3);
      shadow.setOrigin(0, 0);

      // Fondo de la tarjeta
      const bg = this.add.rectangle(0, 0, cardW, cardH, 0x0d0d2b, 0.9)
        .setStrokeStyle(2, cls.color, 0.6)
        .setOrigin(0, 0)
        .setInteractive({ useHandCursor: true });
      container.add(shadow);
      container.add(bg);

      // Color band arriba
      const colorBand = this.add.rectangle(0, 0, cardW, 6, cls.color, 0.8);
      colorBand.setOrigin(0, 0);
      container.add(colorBand);

      // Preview de peonza (círculo de color)
      const previewG = this.add.graphics();
      previewG.fillStyle(cls.color, 0.3);
      previewG.fillCircle(cardW / 2, 45, 28);
      previewG.lineStyle(3, cls.color, 0.8);
      previewG.strokeCircle(cardW / 2, 45, 28);
      previewG.fillStyle(Phaser.Display.Color.ValueToColor(cls.color).darken(30).color, 0.5);
      previewG.fillCircle(cardW / 2, 45, 18);
      previewG.fillStyle(0xffffff, 0.4);
      previewG.fillCircle(cardW / 2, 45, 8);
      container.add(previewG);

      // Nombre
      const nameT = this.add.text(cardW / 2, 85, cls.name, {
        fontSize: '15px', color: '#ffffff', fontStyle: 'bold',
      }).setOrigin(0.5);
      container.add(nameT);

      // Stats compactas
      this.drawStatBar(container, cardW, 'HP', cls.hp, 200, 0x2ecc71, 100);
      this.drawStatBar(container, cardW, 'ATQ', cls.attack, 15, 0xe74c3c, 112);
      this.drawStatBar(container, cardW, 'VEL', cls.speed, 3, 0x3498db, 124);
      this.drawStatBar(container, cardW, 'DEF', cls.defense, 1.6, 0xf1c40f, 136);

      container.setSize(cardW, cardH);

      // Eventos hover
      bg.on('pointerover', () => {
        bg.setFillStyle(0x1a1a4e, 1);
        bg.setStrokeStyle(3, cls.color, 1);
        this.showClassDetail(cls);
        //this.drawPreviewTop(cls);
      });
      bg.on('pointerout', () => {
        bg.setFillStyle(0x0d0d2b, 0.9);
        bg.setStrokeStyle(2, cls.color, 0.6);
      });
      bg.on('pointerdown', () => {
        this.selectedClass = cls.id;
        this.phase = 'special';
        this.labelPhase.setText('PASO 2: ELIGE UN ESPECIAL');
        this.showSpecialSelection();
      });

      this.classCards.push(container);
    });
  }

  private drawStatBar(container: Phaser.GameObjects.Container, cardW: number, label: string, value: number, max: number, color: number, y: number): void {
    const barX = 10;
    const barW = cardW - 20;
    const barH = 9;
    const pct = Math.min(value / max, 1);

    const lbl = this.add.text(barX, y, `${label}`, {
      fontSize: '10px', color: '#95a5a6',
    });
    container.add(lbl);

    // Fondo barra
    const barBg = this.add.rectangle(barX + 22, y, barW - 22, barH, 0x1a1a3e, 0.8);
    barBg.setOrigin(0, 0);
    container.add(barBg);

    // Barra valor
    const barFill = this.add.rectangle(barX + 22, y, (barW - 22) * pct, barH, color, 0.9);
    barFill.setOrigin(0, 0);
    container.add(barFill);

    // Valor numérico
    const valT = this.add.text(barX + 24 + (barW - 24) * pct, y - 1, `${value}`, {
      fontSize: '8px', color: '#ffffff', fontStyle: 'bold',
    });
    container.add(valT);
  }

  private drawPreviewTop(cls: ClassDefinition): void {
    this.previewTopGraphics.clear();
    if (this.previewTopLabel) {
      this.previewTopLabel.destroy();
      this.previewTopLabel = null;
    }

    const x = GAME_WIDTH - 135;
    const y = 500;
    const r = 50;

    // Sombra
    this.previewTopGraphics.fillStyle(0x000000, 0.3);
    this.previewTopGraphics.fillCircle(x + 3, y + 3, r);

    // Cuerpo
    this.previewTopGraphics.fillStyle(cls.color, 0.8);
    this.previewTopGraphics.fillCircle(x, y, r);
    this.previewTopGraphics.fillStyle(Phaser.Display.Color.ValueToColor(cls.color).darken(40).color, 0.8);
    this.previewTopGraphics.fillCircle(x, y, r * 0.6);
    this.previewTopGraphics.fillStyle(0xffffff, 0.5);
    this.previewTopGraphics.fillCircle(x, y, r * 0.3);

    // Nombre grande (con referencia para poder limpiarlo)
    this.previewTopLabel = this.add.text(x, y + r + 20, cls.name, {
      fontSize: '18px', color: '#ffffff', fontStyle: 'bold',
    }).setOrigin(0.5);
  }

  private showClassDetail(cls: ClassDefinition): void {
    this.clearDetailPanel();
    this.detailPanel.add(this.detailPanelBg);

    let dy = 30;

    // Nombre
    this.detailPanel.add(this.add.text(15, dy, cls.name, {
      fontSize: '20px', color: '#ffffff', fontStyle: 'bold',
    }));
    dy += 28;

    // Descripción
    this.detailPanel.add(this.add.text(15, dy, cls.description, {
      fontSize: '11px', color: '#bdc3c7', wordWrap: { width: 220 },
    }));
    dy += 50;

    // Stats detalladas
    const stats = [
      { label: 'HP', value: cls.hp, max: 200, color: 0x2ecc71 },
      { label: 'Ataque', value: cls.attack, max: 15, color: 0xe74c3c },
      { label: 'Velocidad', value: cls.speed, max: 3, color: 0x3498db },
      { label: 'Defensa', value: cls.defense, max: 1.6, color: 0xf1c40f },
      { label: 'Estabilidad', value: cls.stability, max: 1.5, color: 0x9b59b6 },
    ];

    stats.forEach((s) => {
      const pct = Math.min(s.value / s.max, 1);

      this.detailPanel.add(this.add.text(15, dy, s.label, {
        fontSize: '11px', color: '#95a5a6',
      }));
      this.detailPanel.add(this.add.text(90, dy, `${s.value}`, {
        fontSize: '11px', color: '#ffffff', fontStyle: 'bold',
      }));

      // Barra
      const barG = this.add.graphics();
      barG.fillStyle(0x1a1a3e, 0.8);
      barG.fillRect(120, dy + 3, 110, 8);
      barG.fillStyle(s.color, 0.8);
      barG.fillRect(120, dy + 3, 110 * pct, 8);
      this.detailPanel.add(barG);

      dy += 20;
    });

    dy += 15;

    // Pasiva especial
    this.detailPanel.add(this.add.text(15, dy, '✨ PASIVA:', {
      fontSize: '11px', color: '#f1c40f', fontStyle: 'bold',
    }));
    dy += 18;
    this.detailPanel.add(this.add.text(15, dy, cls.specialPassive, {
      fontSize: '10px', color: '#bdc3c7', wordWrap: { width: 220 },
    }));
  }

  private showSpecialSelection(): void {
    // Ocultar clases con animación
    this.classCards.forEach((c) => {
      this.tweens.add({
        targets: c,
        alpha: 0,
        duration: 200,
        onComplete: () => c.setVisible(false),
      });
    });
    this.previewTopGraphics.clear();
    this.clearDetailPanel();

    // Título (destruir el anterior si existe)
    if (this.phaseTitle) this.phaseTitle.destroy();
    this.phaseTitle = this.add.text(30, 100, 'SELECCIONA ESPECIAL', {
      fontSize: '13px', color: '#7f8c8d', fontStyle: 'bold', letterSpacing: 2,
    });

    // Layout cuadrícula: 3 columnas x 3 filas (8 especiales + Sin especial)
    const cols = 3;
    const startX = 180;
    const startY = 180;
    const cardW = 300;
    const cardH = 90;
    const gapX = 15;
    const gapY = 12;

    SPECIALS.forEach((sp, i) => {
      const col = i % cols;
      const row = Math.floor(i / cols);
      const x = startX + col * (cardW + gapX);
      const y = startY + row * (cardH + gapY);

      const container = this.add.container(x, y);

      // Fondo
      const bg = this.add.rectangle(0, 0, cardW, cardH, 0x0d0d2b, 0.9)
        .setStrokeStyle(2, 0x9b59b6, 0.5)
        .setInteractive({ useHandCursor: true });
      container.add(bg);

      // Icono
    //   container.add(this.add.text(-100, -42, `${sp.icon}`, {
    //     fontSize: '28px',
    //   }));

      // Nombre
      container.add(this.add.text(-120, -38, sp.name, {
        fontSize: '15px', color: '#ffffff', fontStyle: 'bold',
      }));

      // Descripción (con wordWrap para que se ajuste al ancho)
      container.add(this.add.text(-120, -26, sp.description, {
        fontSize: '11px', color: '#bdc3c7',
        wordWrap: { width: cardW - 40 },
      }));

      bg.on('pointerover', () => {
        bg.setFillStyle(0x1a1a3e, 1);
        bg.setStrokeStyle(2, 0x9b59b6, 1);
      });
      bg.on('pointerout', () => {
        bg.setFillStyle(0x0d0d2b, 0.9);
        bg.setStrokeStyle(2, 0x9b59b6, 0.5);
      });
      bg.on('pointerdown', () => {
        this.selectedSpecial = sp.id;
        this.confirmSelection();
      });

      this.specialCards.push(container);
    });

    // Botón "Sin especial" en la última posición de la cuadrícula
    const noneIdx = SPECIALS.length; // 8
    const noneCol = noneIdx % cols; // 8 % 3 = 2
    const noneRow = Math.floor(noneIdx / cols); // 8 / 3 = 2
    const nx = startX + noneCol * (cardW + gapX);
    const ny = startY + noneRow * (cardH + gapY);

    const noneContainer = this.add.container(nx, ny);
    const noneBg = this.add.rectangle(0, 0, cardW, cardH, 0x1a1a2e, 0.8)
      .setStrokeStyle(1, 0x7f8c8d, 0.4)
      .setInteractive({ useHandCursor: true });
    noneContainer.add(noneBg);
    noneContainer.add(this.add.text((cardW / 2) - 150, (cardH / 2 - 8) - 60, '❌  SIN ESPECIAL', {
      fontSize: '16px', color: '#7f8c8d', fontStyle: 'bold',
    }).setOrigin(0.5));
    noneContainer.add(this.add.text((cardW / 2) - 150, (cardH / 2 + 12) - 60, '(sin efectos extra)', {
      fontSize: '10px', color: '#5a5a6a',
    }).setOrigin(0.5));
    noneBg.on('pointerover', () => noneBg.setFillStyle(0x2a2a3e, 0.8));
    noneBg.on('pointerout', () => noneBg.setFillStyle(0x1a1a2e, 0.8));
    noneBg.on('pointerdown', () => {
      this.selectedSpecial = '';
      this.confirmSelection();
    });
    this.specialCards.push(noneContainer);

    // Clase seleccionada - mostrar preview arriba a la derecha
    const selectedCls = CLASSES.find((c) => c.id === this.selectedClass) || CLASSES[0];
    this.add.text(GAME_WIDTH - 200, 110, `Clase: ${selectedCls.name}`, {
      fontSize: '16px', color: '#ffffff', fontStyle: 'bold',
    }).setOrigin(0, 0);

    // Preview peonza pequeña
    // const prevG = this.add.graphics();
    // prevG.fillStyle(selectedCls.color, 0.8);
    // prevG.fillCircle(GAME_WIDTH - 135, 165, 35);
    // prevG.fillStyle(Phaser.Display.Color.ValueToColor(selectedCls.color).darken(40).color, 0.8);
    // prevG.fillCircle(GAME_WIDTH - 135, 165, 22);
    // prevG.fillStyle(0xffffff, 0.5);
    // prevG.fillCircle(GAME_WIDTH - 135, 165, 10);
  }

  private confirmSelection(): void {
    const sel = this.selections[this.currentPlayer];
    sel.className = this.selectedClass || CLASSES[0].id;
    sel.specialName = this.selectedSpecial || '';

    this.currentPlayer++;
    this.showCurrentPlayer();
  }

  private selectRandomForIA(index: number): void {
    const sel = this.selections[index];
    const randomClass = CLASSES[Math.floor(Math.random() * CLASSES.length)];
    const randomSpecial = Math.random() > 0.3
      ? SPECIALS[Math.floor(Math.random() * SPECIALS.length)].id
      : '';

    sel.className = randomClass.id;
    sel.specialName = randomSpecial;

    this.currentPlayer++;
    this.showCurrentPlayer();
  }

  private finishSelection(): void {
    // Crear sistema de torneo si aplica
    let torneoSystem: TorneoSystem | undefined;
    if (this.isTorneo) {
      torneoSystem = new TorneoSystem(this.config, this.selections);
    }

    this.scene.start('ArenaSelectScene', {
      config: this.config,
      selections: this.selections,
      torneoSystem,
      isTorneo: this.isTorneo,
    });
  }
}
